import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi, type AgentRun } from "@ncp/api-client";
import { useTranslation } from "@ncp/i18n";
import { useSave } from "@ncp/ui";
import type { DefinitionStatus } from "../forms/api";
import type { ChangeSet } from "../releases/api";
import type { AssistantRow, BuilderRef, GuardrailProfile, ModelTier } from "../workspaces/api";

export type AgentKind = "Configurable" | "Compiled";

/** One step of a multi-step agent; a step with a sub-agent hands its instruction to that agent. */
export interface AgentStep { key: string; title: string; instruction: string; toolKeys: string[]; delegateAgentKey?: string | null }
/** What an approval does besides the proposal's own action: an automation action with its settings. */
export interface OnApproveAction { type: string; [setting: string]: unknown }
/** An agent test case: an input brief (or matter) and what the draft must (not) contain. */
export interface AgentTestCase { name: string; brief?: string; caseNumber?: string; expect: { contains?: string[]; notContains?: string[]; fields?: string[]; maxLength?: number } }

export interface AgentDefinition {
  id: string;
  key: string;
  version: number;
  status: DefinitionStatus;
  kind: AgentKind;
  compiledAgentKey?: string | null;
  name: string;
  nameAr?: string | null;
  description?: string | null;
  descriptionAr?: string | null;
  tags: string[];
  modelTier: ModelTier;
  modelId?: string | null;
  systemPrompt: string;
  userPromptTemplate: string;
  toolKeys: string[];
  connectorOperationKeys: string[];
  retrievalSources: string[];
  outputSchema: Record<string, unknown> | null;
  proposedActionType: string;
  reviewerRoleKey: string;
  guardrailProfile: GuardrailProfile;
  maxOutputTokens: number;
  runTokenBudget: number;
  monthlyBudgetUsd: number;
  temperature: number;
  invokedFromNodes: string[];
  enabled: boolean;
  steps?: AgentStep[] | null;
  delegateAgentKeys?: string[] | null;
  onApproveActions?: OnApproveAction[] | null;
  testCases?: AgentTestCase[] | null;
  changeNote?: string | null;
  createdByName?: string | null;
  createdAt: string;
  updatedAt: string;
  publishedAt?: string | null;
  publishedByName?: string | null;
  publishedByChangeSetId?: string | null;
  sourcePackageKey?: string | null;
  usedByStages: string[];
  usedByAssistants: string[];
}

/** The editable part of a definition, as sent to PUT /api/platform/agents/{id}. */
export interface AgentBody {
  name: string;
  nameAr: string | null;
  description: string | null;
  descriptionAr: string | null;
  tags: string[];
  kind: AgentKind;
  modelTier: ModelTier;
  modelId: string | null;
  systemPrompt: string;
  userPromptTemplate: string;
  toolKeys: string[];
  connectorOperationKeys: string[];
  retrievalSources: string[];
  outputSchema: Record<string, unknown>;
  proposedActionType: string;
  reviewerRoleKey: string;
  guardrailProfile: GuardrailProfile;
  maxOutputTokens: number;
  runTokenBudget: number;
  monthlyBudgetUsd: number;
  temperature: number;
  enabled: boolean;
  steps: AgentStep[];
  delegateAgentKeys: string[];
  onApproveActions: OnApproveAction[];
  testCases: AgentTestCase[];
}

export function agentBodyOf(d: AgentDefinition): AgentBody {
  return {
    name: d.name, nameAr: d.nameAr ?? null, description: d.description ?? null, descriptionAr: d.descriptionAr ?? null, tags: d.tags, kind: d.kind,
    modelTier: d.modelTier, modelId: d.modelId ?? null, systemPrompt: d.systemPrompt, userPromptTemplate: d.userPromptTemplate, toolKeys: d.toolKeys,
    connectorOperationKeys: d.connectorOperationKeys, retrievalSources: d.retrievalSources, outputSchema: (d.outputSchema as Record<string, unknown> | null) ?? { type: "object" },
    proposedActionType: d.proposedActionType, reviewerRoleKey: d.reviewerRoleKey, guardrailProfile: d.guardrailProfile, maxOutputTokens: d.maxOutputTokens,
    runTokenBudget: d.runTokenBudget, monthlyBudgetUsd: d.monthlyBudgetUsd, temperature: d.temperature, enabled: d.enabled,
    steps: (d.steps ?? []).map((s) => ({ ...s, toolKeys: s.toolKeys ?? [] })), delegateAgentKeys: d.delegateAgentKeys ?? [], onApproveActions: d.onApproveActions ?? [], testCases: d.testCases ?? [],
  };
}

export const EMPTY_AGENT: AgentBody = {
  name: "", nameAr: null, description: null, descriptionAr: null, tags: [], kind: "Configurable", modelTier: "Reasoning", modelId: null,
  systemPrompt: "", userPromptTemplate: "{{#matter}}\n{{matter}}\n\nDOCUMENTS\n{{documents}}\n{{/matter}}\n{{#nomatter}}\nBRIEF\n{{brief}}\n{{/nomatter}}",
  toolKeys: ["working_days"], connectorOperationKeys: [], retrievalSources: ["matter", "case-documents"], outputSchema: { title: "Draft", type: "object" },
  proposedActionType: "matter.document.file:Report", reviewerRoleKey: "Stage", guardrailProfile: "Standard", maxOutputTokens: 8192, runTokenBudget: 0, monthlyBudgetUsd: 0,
  temperature: 0.2, enabled: true, steps: [], delegateAgentKeys: [], onApproveActions: [], testCases: [],
};

export interface AgentCatalogRow {
  key: string;
  name: string;
  nameAr?: string | null;
  description?: string | null;
  kind: AgentKind;
  /** "Configured" for data-defined agents; the compiled category (WorkflowAgent, Assistant) otherwise. */
  category: string;
  tags: string[];
  definition?: AssistantRow["summary"] | null;
  modelTier: string;
  reviewer?: string | null;
  enabled: boolean;
  usedByStages: string[];
  usedByAssistants: string[];
  runsMonth: number;
  proposalsMonth: number;
  acceptanceRate?: number | null;
  spendMonthUsd: number;
  lastRunAt?: string | null;
}

export interface AgentToolInfo { key: string; purpose: string; needsCase: boolean }
export interface AgentActionOption { type: string; argument: string; description: string }
export interface AgentPlaceholder { token: string; description: string }
export interface AgentBuilderOptions {
  tools: AgentToolInfo[];
  knowledgeCollections: BuilderRef[];
  roles: BuilderRef[];
  actions: AgentActionOption[];
  workspaces: BuilderRef[];
  modelTiers: ModelTier[];
  guardrailProfiles: GuardrailProfile[];
  placeholders: AgentPlaceholder[];
  blocks: AgentPlaceholder[];
  recordTypes?: BuilderRef[];
  services?: BuilderRef[];
  letterTemplates?: BuilderRef[];
}

export interface AgentPromptPreview { instructions: string; task: string; tools: string[]; collections: string[] }
export interface AgentSampleMatter { id: string; number: string; title: string; playbookKey?: string | null; state: string; updatedAt: string }
export interface AgentTestRunRow { id: string; status: AgentRun["status"]; version?: number | null; matterNumber?: string | null; brief?: string | null; startedAt: string; completedAt?: string | null; tokens: number; costUsd: number; error?: string | null }

/** Payload of the Draft step a sandboxed run records instead of filing a proposal. */
export interface AgentTestDraft {
  kind: string;
  title: string;
  titleAr?: string | null;
  summary: string;
  summaryAr?: string | null;
  contentMarkdown: string;
  contentMarkdownAr?: string | null;
  structured?: unknown;
  action?: { type: string; label?: string | null; labelAr?: string | null; payload?: unknown } | null;
  confidence: number;
  urgency: string;
  requiredRole?: string | null;
  flags: string[];
  quality?: { score: number; verdict: string; issues: string[] } | null;
  sources: Array<{ index: number; title: string; locator?: string | null; excerpt: string }>;
  unsupported: string[];
}

const AG = ["platform", "agents"] as const;

export function useAgentCatalog(q?: string) {
  const api = useApi();
  return useQuery({ queryKey: [...AG, "list", q ?? ""], queryFn: () => api.get<AgentCatalogRow[]>(`/api/platform/agents${q ? `?q=${encodeURIComponent(q)}` : ""}`), staleTime: 10_000 });
}

export function useAgentDefinition(key: string | undefined, version?: number) {
  const api = useApi();
  return useQuery({
    queryKey: [...AG, "one", key, version ?? "latest"],
    queryFn: () => api.get<AgentDefinition>(`/api/platform/agents/${encodeURIComponent(key!)}${version ? `?version=${version}` : ""}`),
    enabled: !!key,
    staleTime: 5_000,
    retry: false,
  });
}

export function useAgentBuilderOptions() {
  const api = useApi();
  return useQuery({ queryKey: [...AG, "options"], queryFn: () => api.get<AgentBuilderOptions>("/api/platform/agents/options"), staleTime: 60_000 });
}

export function useAgentActions() {
  const api = useApi();
  const qc = useQueryClient();
  const { t } = useTranslation();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: [...AG] });
    void qc.invalidateQueries({ queryKey: ["platform", "change-sets"] });
    void qc.invalidateQueries({ queryKey: ["platform", "definitions"] });
    void qc.invalidateQueries({ queryKey: ["platform", "assistants", "options"] });
  };
  return {
    create: useSave({ mutationFn: (input: { key: string; body: AgentBody; changeNote?: string }) => api.post<AgentDefinition>("/api/platform/agents", input), onSuccess: invalidate }),
    duplicate: useSave({ mutationFn: (input: { key: string; newKey: string; name?: string }) => api.post<AgentDefinition>(`/api/platform/agents/${encodeURIComponent(input.key)}/duplicate`, { newKey: input.newKey, name: input.name }), onSuccess: invalidate }),
    update: useSave({ mutationFn: (input: { id: string; body: AgentBody; changeNote?: string }) => api.put<AgentDefinition>(`/api/platform/agents/${input.id}`, { body: input.body, changeNote: input.changeNote }), onSuccess: invalidate }),
    newVersion: useSave({ mutationFn: (input: { key: string; changeNote?: string }) => api.post<AgentDefinition>(`/api/platform/agents/${encodeURIComponent(input.key)}/versions`, { changeNote: input.changeNote }), onSuccess: invalidate }),
    discard: useSave({ mutationFn: (id: string) => api.delete<void>(`/api/platform/agents/${id}`), onSuccess: invalidate }),
    stage: useSave({ mutationFn: (input: { id: string; titleHint?: string }) => api.post<ChangeSet>(`/api/platform/agents/${input.id}/stage`, { titleHint: input.titleHint }), onSuccess: invalidate }),
    withdraw: useSave({ mutationFn: (id: string) => api.post<AgentDefinition>(`/api/platform/agents/${id}/withdraw`), onSuccess: invalidate }),
    // A preview and a test run store no definition: their failure is not a "Not saved".
    preview: useSave({ mutationFn: (input: { definitionId: string; matterId?: string | null; brief?: string | null; workspaceKey?: string | null }) => api.post<AgentPromptPreview>("/api/platform/agents/test-bench/preview", input), failed: t("admin:common.error") }),
    run: useSave({
      mutationFn: (input: { definitionId: string; matterId?: string | null; brief?: string | null; workspaceKey?: string | null }) => api.post<void>("/api/platform/agents/test-bench/runs", input),
      failed: t("admin:common.error"),
      onSuccess: () => void qc.invalidateQueries({ queryKey: [...AG, "test-runs"] }),
    }),
  };
}

export function useAgentSampleMatters(key: string | undefined) {
  const api = useApi();
  return useQuery({ queryKey: [...AG, "samples", key], queryFn: () => api.get<AgentSampleMatter[]>(`/api/platform/agents/${encodeURIComponent(key!)}/sample-matters`), enabled: !!key, staleTime: 30_000 });
}

export function useAgentTestRuns(key: string | undefined, poll: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: [...AG, "test-runs", key],
    queryFn: () => api.get<AgentTestRunRow[]>(`/api/platform/agents/${encodeURIComponent(key!)}/test-runs`),
    enabled: !!key,
    refetchInterval: poll ? 3_000 : false,
  });
}

export interface AgentEvaluationResult { name: string; kind: "case" | "redteam"; passed: boolean; detail: string; output?: string | null; runId?: string | null }
export interface AgentEvaluation {
  id: string; agentKey: string; definitionId: string; version: number; passed: number; total: number; redTeamPassed: number; redTeamTotal: number;
  succeeded: boolean; results: AgentEvaluationResult[] | null; runByName?: string | null; durationMs: number; createdAt: string;
}
export interface RedTeamProbe { key: string; name: string; brief: string }

export function useAgentEvaluations(key: string | undefined, version: number | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: [...AG, "evaluations", key, version],
    queryFn: () => api.get<AgentEvaluation[]>(`/api/platform/agents/${encodeURIComponent(key!)}/evaluations${version ? `?version=${version}` : ""}`),
    enabled: !!key,
  });
}

export function useRedTeamPack() {
  const api = useApi();
  return useQuery({ queryKey: [...AG, "red-team"], queryFn: () => api.get<RedTeamProbe[]>("/api/platform/agents/red-team"), staleTime: Infinity });
}

export function useRunEvaluation() {
  const api = useApi();
  const qc = useQueryClient();
  const { t } = useTranslation();
  return useSave({
    // A failed evaluation (`succeeded: false`) is the gate's result, not a refusal of the request: the answer is handed on wrapped, so the
    // save helper does not read it as one.
    mutationFn: async (definitionId: string) => ({ evaluation: await api.post<AgentEvaluation>(`/api/platform/agents/${definitionId}/evaluate`) }),
    failed: t("admin:common.error"),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: [...AG, "evaluations"] }); void qc.invalidateQueries({ queryKey: ["platform", "change-sets"] }); },
  });
}

export function useAgentTestRun(id: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: [...AG, "test-run", id],
    queryFn: () => api.get<AgentRun>(`/api/platform/agents/test-bench/runs/${id}`),
    enabled: !!id,
    refetchInterval: (q) => (q.state.data?.status === "Running" || q.state.data?.status === "Queued" ? 2_500 : false),
  });
}
